import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  message,
  Space,
  Layout,
  Typography,
  Spin,
  Modal,
  Descriptions,
  Divider,
  Tooltip,
  Input,
  Steps,
  Alert,
  Timeline,
  Badge,
  Progress,
  Switch,
  Radio,
  Dropdown,
  Menu,
  Select,
  Popconfirm
} from 'antd';
import {
  FileDoneOutlined,
  EyeOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  CheckOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  HistoryOutlined,
  UserOutlined,
  FieldTimeOutlined,
  EnvironmentOutlined,
  ApartmentOutlined,
  BankOutlined,
  RedoOutlined,
  SyncOutlined,
  DesktopOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import Navbar from "./Navbar";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './WorkflowDashboard.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Step } = Steps;
const { Option } = Select;

const API_BASE = process.env.REACT_APP_API_BASE_URL + '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 300000
});

// Helper function to get user ID from sessionStorage (handles both formats from Login)
const getUserIdFromSession = () => {
  let userId = sessionStorage.getItem('userId');
  if (!userId || userId === 'null' || userId === 'undefined') {
    userId = sessionStorage.getItem('user_id');
  }
  if (!userId || userId === 'null' || userId === 'undefined') {
    try {
      const userData = JSON.parse(sessionStorage.getItem('user') || '{}');
      userId = userData.id || userData.user_id;
    } catch(e) {}
  }
  return userId;
};

// Function to decode JWT token and extract desk_ids
const decodeToken = (token) => {
  try {
    if (!token) return null;
    
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const decoded = JSON.parse(jsonPayload);
    
    // Normalize user ID field for consistent access
    decoded.user_id = decoded.user_id || decoded.id || decoded.userId;
    
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Helper function to check if session is valid before API calls
const checkSession = (navigate) => {
  const token = sessionStorage.getItem('token');
  const userId = getUserIdFromSession();
  
  if (!token || !userId) {
    console.warn('⚠️ Session check failed - no token or user ID');
    if (navigate) {
      navigate('/login');
    }
    return false;
  }
  
  return true;
};

// Add response interceptor for 401 handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.warn('⚠️ Authentication failed (401) - clearing session');
      sessionStorage.clear();
      
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
        message.error('Session expired. Please login again.');
      }
    }
    return Promise.reject(error);
  }
);

api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token');
  let userRole = sessionStorage.getItem('userRole') || 'ULB';
  let userId = getUserIdFromSession();

  const userDivision = sessionStorage.getItem('division_id') || sessionStorage.getItem('division');
  const userDistrict = sessionStorage.getItem('district_id') || sessionStorage.getItem('district');
  const userDeskId = sessionStorage.getItem('desk_id') || sessionStorage.getItem('deskId');
  
  // Get ALL desk IDs from sessionStorage
  let userDeskIds = [];
  const deskIdsStr = sessionStorage.getItem('desk_ids') || sessionStorage.getItem('deskIds');
  
  if (deskIdsStr) {
    try {
      userDeskIds = JSON.parse(deskIdsStr);
      if (!Array.isArray(userDeskIds)) {
        userDeskIds = [userDeskIds];
      }
    } catch(e) {
      if (deskIdsStr.includes(',')) {
        userDeskIds = deskIdsStr.split(',').map(id => id.trim()).filter(id => id && id !== 'null' && id !== 'undefined');
      } else if (deskIdsStr && deskIdsStr !== 'null' && deskIdsStr !== 'undefined') {
        userDeskIds = [deskIdsStr];
      }
    }
  }
  
  // IMPORTANT FIX: Don't add the single desk ID if it's already in the array
  if (userDeskId && userDeskId !== 'null' && userDeskId !== 'undefined' && userDeskId !== '') {
    if (!userDeskIds.includes(userDeskId)) {
      userDeskIds.push(userDeskId);
    }
  }
  
  // Remove duplicates and filter out invalid IDs
  userDeskIds = [...new Set(userDeskIds)];
  userDeskIds = userDeskIds.filter(id => id && id !== 'null' && id !== 'undefined' && id !== '');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers['x-user-role'] = userRole;

  if (userId && userId !== 'null' && userId !== 'undefined') {
    config.headers['x-user-id'] = userId;
  }

  if (userDivision && userDivision !== 'null' && userDivision !== 'undefined' && userDivision !== '') {
    config.headers['x-user-division'] = userDivision;
  }

  if (userDistrict && userDistrict !== 'null' && userDistrict !== 'undefined' && userDistrict !== '') {
    config.headers['x-user-district'] = userDistrict;
  }

  if (userDeskIds.length > 0) {
    const deskIdsString = userDeskIds.join(',');
    config.headers['x-user-desk-id'] = deskIdsString;
    console.log(`📎 Adding ${userDeskIds.length} unique desk_ids as comma-separated: ${deskIdsString}`);
  } else {
    if (userDeskId && userDeskId !== 'null' && userDeskId !== 'undefined' && userDeskId !== '') {
      config.headers['x-user-desk-id'] = userDeskId;
      console.log(`📎 Adding single desk_id: ${userDeskId}`);
    }
  }

  return config;
}, error => {
  return Promise.reject(error);
});

const WorkflowDashboard = () => {
  const navigate = useNavigate();
  const [submissionsPageSize, setSubmissionsPageSize] = useState(10);
  const [submissionsCurrentPage, setSubmissionsCurrentPage] = useState(1);

  const [instances, setInstances] = useState([]);
  const [filteredInstances, setFilteredInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Auto-refresh states
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [refreshCountdown, setRefreshCountdown] = useState(0);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  
  // Refs for modal state tracking
  const isModalOpenRef = useRef(false);
  const dataViewRecordRef = useRef(null);
  const fieldOrderCacheRef = useRef({});
  
  const EXCLUDED_TABLE_FIELDS = new Set(['frequency_enabled', 'frequency_type', 'frequency_value', 'frequency_period', 
    'frequency_period_label', 'frequency_selected_at', 'frequency_unit', 'frequency_custom','id',  'user_role', 'user_name', 'user_id',
    'district_id', 'division_id', 'ulb_id', 'created_at', 'updated_at',
    'user_district_name', 'user_division_name', 'ulb_name', 'status', 'progress','schedule_time','schedule_date_format','workflow_status'
    ]);

  // Submission filters
  const [submissionFilters, setSubmissionFilters] = useState({
    division: null,
    district: null,
    ulb: null
  });
  const [availableFilters, setAvailableFilters] = useState({
    divisions: [],
    districts: [],
    ulbs: []
  });

  // Bulk approval states
  const [bulkApproveModal, setBulkApproveModal] = useState({
    visible: false,
    loading: false,
    divisions: [],
    selectedDivision: null,
    pendingSubmissions: [],
    comments: ''
  });

  const [userInfo, setUserInfo] = useState({
    id: null,
    name: '',
    role: '',
    roleType: 'ULB',
    district: null,
    division: null,
    deskId: null,
    deskIds: []
  });

  const [detailsModal, setDetailsModal] = useState({
    visible: false,
    loading: false,
    instance: null,
    submissionData: null,
    allSubmissions: [],
    tableColumns: [],
    tableData: [],
    selectedRowKeys: [],
    workflowData: null,
    canTakeAction: false,
    currentStage: null,
    stageInfo: null,
    isDMADeskUser: false
  });

  const [actionLoading, setActionLoading] = useState(false);
  const [actionModal, setActionModal] = useState({
    visible: false,
    submission: null,
    action: null,
    comments: ''
  });

  const [historyModal, setHistoryModal] = useState({
    visible: false,
    submissionId: null,
    actions: [],
    loading: false
  });

  const [dataViewModal, setDataViewModal] = useState({
    visible: false,
    record: null,
    loading: false
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [formFieldOrder, setFormFieldOrder] = useState([]);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [fieldOrderLoading, setFieldOrderLoading] = useState(false);
  const [currentViewFormId, setCurrentViewFormId] = useState(null);

  // UPDATED: Force re-render when field order loads
  useEffect(() => {
    if (dataViewModal.visible && dataViewModal.record && formFieldOrder.length > 0) {
      console.log('🔄 Field order loaded, updating modal state...');
      if (dataViewModal.loading) {
        setDataViewModal(prev => ({
          ...prev,
          loading: false
        }));
      }
    }
  }, [formFieldOrder, dataViewModal.visible, dataViewModal.record]);

  // Helper function to get user name from record
  const getUserName = (record) => {
    if (!record) return 'Unknown User';

    const userName = record.user_name ||
                     record.submitted_by_name ||
                     record.created_by_name ||
                     record.name ||
                     record.username;

    if (userName && userName !== 'null' && userName !== 'undefined' && userName !== `User ${record.user_id}`) {
      return userName;
    }

    if (record.user_id) {
      return `User ${record.user_id}`;
    }

    return 'Unknown User';
  };

  // Helper function to check if submission is a draft
  const isDraftSubmission = (submission) => {
    if (submission.status === 'draft' || submission.workflow_status === 'draft') {
      return true;
    }
    if (submission.draft === true || submission.draft === 1 || submission.draft === '1') {
      return true;
    }
    return false;
  };

  const [cascadingFilters, setCascadingFilters] = useState({
    filteredDistricts: [],
    filteredUlbs: []
  });

  const extractFilterOptions = (submissions) => {
    const divisions = new Set();
    const districts = new Set();
    const ulbs = new Set();

    submissions.forEach(sub => {
      const divisionName = sub.user_division_name || sub.division_name || sub.user_division;
      if (divisionName && divisionName !== 'null' && divisionName !== 'undefined') {
        divisions.add(divisionName);
      }

      const districtName = sub.user_district_name || sub.district_name || sub.user_district;
      if (districtName && districtName !== 'null' && districtName !== 'undefined') {
        districts.add(districtName);
      }

      const ulbName = sub.ulb_name || sub.ulb_name_display || sub.user_ulb_name;
      if (ulbName && ulbName !== 'null' && ulbName !== 'undefined') {
        ulbs.add(ulbName);
      }
    });

    setAvailableFilters({
      divisions: Array.from(divisions).sort(),
      districts: Array.from(districts).sort(),
      ulbs: Array.from(ulbs).sort()
    });
  };

  const getFilteredDistricts = (submissions, selectedDivision) => {
    if (!selectedDivision) return availableFilters.districts;
    
    const districts = new Set();
    submissions.forEach(sub => {
      const divisionName = sub.user_division_name || sub.division_name || sub.user_division;
      const districtName = sub.user_district_name || sub.district_name || sub.user_district;
      
      if (divisionName === selectedDivision && districtName && districtName !== 'null' && districtName !== 'undefined') {
        districts.add(districtName);
      }
    });
    
    return Array.from(districts).sort();
  };

  const getFilteredUlbs = (submissions, selectedDivision, selectedDistrict) => {
    if (!selectedDivision && !selectedDistrict) return availableFilters.ulbs;
    
    const ulbs = new Set();
    submissions.forEach(sub => {
      const divisionName = sub.user_division_name || sub.division_name || sub.user_division;
      const districtName = sub.user_district_name || sub.district_name || sub.user_district;
      const ulbName = sub.ulb_name || sub.ulb_name_display || sub.user_ulb_name;
      
      let matches = true;
      if (selectedDivision && divisionName !== selectedDivision) matches = false;
      if (selectedDistrict && districtName !== selectedDistrict) matches = false;
      
      if (matches && ulbName && ulbName !== 'null' && ulbName !== 'undefined') {
        ulbs.add(ulbName);
      }
    });
    
    return Array.from(ulbs).sort();
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...submissionFilters, [filterType]: value };
    
    if (filterType === 'division') {
      newFilters.district = null;
      newFilters.ulb = null;
    } else if (filterType === 'district') {
      newFilters.ulb = null;
    }
    
    setSubmissionFilters(newFilters);
    
    const allSubmissions = detailsModal.allSubmissions || [];
    const filteredDistricts = getFilteredDistricts(allSubmissions, newFilters.division);
    const filteredUlbs = getFilteredUlbs(allSubmissions, newFilters.division, newFilters.district);
    
    setCascadingFilters({
      filteredDistricts,
      filteredUlbs
    });
    
    const filtered = filterSubmissions(allSubmissions, newFilters);
    setDetailsModal(prev => ({
      ...prev,
      tableData: filtered,
      selectedRowKeys: []
    }));
  };

  const clearAllFilters = () => {
    setSubmissionFilters({ division: null, district: null, ulb: null });
    setCascadingFilters({
      filteredDistricts: [],
      filteredUlbs: []
    });
    setDetailsModal(prev => ({
      ...prev,
      tableData: prev.allSubmissions || [],
      selectedRowKeys: []
    }));
  };

  useEffect(() => {
    if (detailsModal.allSubmissions && detailsModal.allSubmissions.length > 0) {
      const filteredDistricts = getFilteredDistricts(detailsModal.allSubmissions, submissionFilters.division);
      const filteredUlbs = getFilteredUlbs(detailsModal.allSubmissions, submissionFilters.division, submissionFilters.district);
      setCascadingFilters({
        filteredDistricts,
        filteredUlbs
      });
    }
  }, [detailsModal.allSubmissions, submissionFilters.division, submissionFilters.district]);

  const filterSubmissions = (submissions, filters) => {
    return submissions.filter(sub => {
      if (filters.division) {
        const divisionName = sub.user_division_name || sub.division_name || sub.user_division;
        if (divisionName !== filters.division) return false;
      }

      if (filters.district) {
        const districtName = sub.user_district_name || sub.district_name || sub.user_district;
        if (districtName !== filters.district) return false;
      }

      if (filters.ulb) {
        const ulbName = sub.ulb_name || sub.ulb_name_display || sub.user_ulb_name;
        if (ulbName !== filters.ulb) return false;
      }

      return true;
    });
  };

  const getUniqueDivisions = (submissions) => {
    const divisions = new Set();
    submissions.forEach(sub => {
      if (sub.current_stage === 'DMA_DESK_OFFICER' &&
          sub.workflow_status !== 'approved' &&
          sub.workflow_status !== 'rejected' &&
          sub.user_id !== userInfo.id) {
        const divisionName = sub.user_division_name || sub.division_name || sub.user_division;
        if (divisionName && divisionName !== 'null' && divisionName !== 'undefined') {
          divisions.add(divisionName);
        }
      }
    });
    return Array.from(divisions).sort();
  };

  const getPendingSubmissionsByDivision = (submissions, divisionName) => {
    return submissions.filter(sub => {
      const subDivisionName = sub.user_division_name || sub.division_name || sub.user_division;
      return sub.current_stage === 'DMA_DESK_OFFICER' &&
             sub.workflow_status !== 'approved' &&
             sub.workflow_status !== 'rejected' &&
             sub.user_id !== userInfo.id &&
             subDivisionName === divisionName;
    });
  };

  const openBulkApproveModal = () => {
    const allSubmissions = detailsModal.allSubmissions || [];
    const divisions = getUniqueDivisions(allSubmissions);

    if (divisions.length === 0) {
      message.info('No pending submissions available for bulk approval');
      return;
    }

    setBulkApproveModal({
      visible: true,
      loading: false,
      divisions: divisions,
      selectedDivision: divisions[0],
      pendingSubmissions: getPendingSubmissionsByDivision(allSubmissions, divisions[0]),
      comments: ''
    });
  };

  const handleDivisionChange = (divisionName) => {
    const pendingSubmissions = getPendingSubmissionsByDivision(detailsModal.allSubmissions || [], divisionName);
    setBulkApproveModal(prev => ({
      ...prev,
      selectedDivision: divisionName,
      pendingSubmissions: pendingSubmissions
    }));
  };

  const handleBulkApprove = async () => {
    const { selectedDivision, pendingSubmissions, comments } = bulkApproveModal;

    if (pendingSubmissions.length === 0) {
      message.warning('No pending submissions in this division');
      return;
    }

    setBulkApproveModal(prev => ({ ...prev, loading: true }));

    let successCount = 0;
    let failCount = 0;
    const errors = [];

    const instanceId = detailsModal.instance?.id;

    if (!instanceId) {
      message.error('No workflow instance found');
      setBulkApproveModal(prev => ({ ...prev, loading: false }));
      return;
    }

    for (const submission of pendingSubmissions) {
      try {
        console.log(`📝 Bulk approving submission: ${submission.id} in division ${selectedDivision}`);

        const response = await api.post('/workflow/submission-action', {
          workflow_instance_id: instanceId,
          submission_id: submission.id,
          action: 'approve',
          comments: comments || `Bulk approval for division: ${selectedDivision}`
        });

        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
          errors.push(`Submission #${submission.id}: ${response.data.message}`);
        }
      } catch (error) {
        failCount++;
        errors.push(`Submission #${submission.id}: ${error.response?.data?.message || error.message}`);
        console.error(`Failed to approve submission ${submission.id}:`, error);
      }
    }

    setBulkApproveModal(prev => ({ ...prev, loading: false }));

    if (successCount > 0) {
      message.success(
        `Bulk approval completed: ${successCount} approved, ${failCount} failed`,
        5
      );

      await refreshSubmissionDetails();
      loadData(true);
      setBulkApproveModal(prev => ({ ...prev, visible: false }));
    } else {
      message.error(`Bulk approval failed for all ${failCount} submissions`);
    }

    if (errors.length > 0 && errors.length <= 5) {
      console.error('Bulk approval errors:', errors);
      Modal.error({
        title: 'Some submissions failed',
        content: (
          <div>
            <p>The following submissions failed to approve:</p>
            <ul style={{ maxHeight: '200px', overflow: 'auto' }}>
              {errors.map((err, idx) => (
                <li key={idx} style={{ wordBreak: 'break-word' }}>{err}</li>
              ))}
            </ul>
          </div>
        ),
        width: 500
      });
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    let userId = getUserIdFromSession();
    const userRole = sessionStorage.getItem('userRole');
    
    console.log('🔐 Session Validation Check:', { hasToken: !!token, userId, userRole });
    
    if (!token || !userId) {
      console.warn('⚠️ No valid session found - redirecting to login');
      message.warning('Please login to continue');
      navigate('/login');
      return;
    }
    
    console.log('✅ Session validated successfully for user:', userId);
  }, [navigate]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      console.warn('⚠️ No token found, redirecting to login');
      navigate('/login');
      return;
    }
    
    const userStr = sessionStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};

    let deskId = null;
    let deskIds = [];
    let userRole = 'ULB';

    if (token) {
      const decodedToken = decodeToken(token);
      console.log('🔐 Decoded token:', decodedToken);

      if (decodedToken) {
        if (decodedToken.role_name) {
          userRole = decodedToken.role_name;
        } else if (decodedToken.role) {
          userRole = decodedToken.role;
        }

        if (decodedToken.desk_ids) {
          deskIds = decodedToken.desk_ids;
          deskId = deskIds.length > 0 ? deskIds[0] : null;
          console.log(`📎 Extracted ${deskIds.length} desk_ids from token:`, deskIds);
        }
      }
    }

    if (!userRole || userRole === 'ULB') {
      userRole = sessionStorage.getItem('userRole') || user.workflow_role || user.role_name || 'ULB';
    }

    if (deskIds.length === 0 && user.desk_ids && user.desk_ids.length > 0) {
      deskIds = user.desk_ids;
      deskId = deskIds[0];
    }

    if (deskIds.length === 0 && user.desk_assignments && user.desk_assignments.length > 0) {
      deskId = user.desk_assignments[0].id;
      deskIds = user.desk_assignments.map(d => d.id);
    }
    
    if (deskIds.length === 0) {
      const storedDeskIds = sessionStorage.getItem('desk_ids') || sessionStorage.getItem('deskIds');
      if (storedDeskIds) {
        try {
          deskIds = JSON.parse(storedDeskIds);
          if (!Array.isArray(deskIds)) {
            deskIds = [deskIds];
          }
          deskId = deskIds[0];
        } catch(e) {
          if (storedDeskIds.includes(',')) {
            deskIds = storedDeskIds.split(',').map(id => id.trim());
            deskId = deskIds[0];
          } else if (storedDeskIds && storedDeskIds !== 'null' && storedDeskIds !== 'undefined') {
            deskIds = [storedDeskIds];
            deskId = storedDeskIds;
          }
        }
      }
    }

    const userId = getUserIdFromSession();
    const userName = sessionStorage.getItem('userName') || user.username || user.name || 'User';
    const userDistrict = sessionStorage.getItem('district_id') || sessionStorage.getItem('district') || user.district;
    const userDivision = sessionStorage.getItem('division_id') || sessionStorage.getItem('division') || user.division;

    let roleType = 'ULB';
    const roleUpper = userRole.toUpperCase();

    console.log('🔍 Detecting role from:', userRole, 'upper:', roleUpper);

    if (roleUpper === 'ADMIN' || roleUpper === 'SUPER_ADMIN' || roleUpper === 'SUPERADMIN') {
      roleType = 'ADMIN';
    } else if (roleUpper === 'DIVISIONAL_OFFICER' || roleUpper === 'RDMA') {
      roleType = 'DIVISIONAL_OFFICER';
    } else if (roleUpper === 'DMA_DESK_OFFICER' || roleUpper === 'DMA') {
      roleType = 'DMA_DESK_OFFICER';
    } else if (roleUpper === 'DMA_DESK_USER' || roleUpper === 'DMA_USER') {
      roleType = 'DMA_DESK_USER';
      console.log('✅ Detected DMA Desk User role with', deskIds.length, 'desks assigned');
    } else if (roleUpper === 'DAO' || roleUpper === 'DISTRICT_ADMIN') {
      roleType = 'DAO';
    }

    if (deskIds.length > 0) {
      sessionStorage.setItem('desk_ids', JSON.stringify(deskIds));
      sessionStorage.setItem('deskIds', JSON.stringify(deskIds));
      if (deskId) {
        sessionStorage.setItem('desk_id', deskId);
        sessionStorage.setItem('deskId', deskId);
      }
      console.log(`💾 Saved ${deskIds.length} desk_ids to storage:`, deskIds);
    }

    sessionStorage.setItem('userRole', userRole);

    setUserInfo({
      id: userId,
      name: userName,
      role: userRole,
      roleType: roleType,
      district: userDistrict,
      division: userDivision,
      deskId: deskId,
      deskIds: deskIds
    });

    console.log('👤 User Info Loaded:', {
      id: userId,
      roleType: roleType,
      role: userRole,
      district: userDistrict,
      division: userDivision,
      deskId: deskId,
      deskIds: deskIds,
      deskCount: deskIds.length
    });

    loadData();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // FIXED: Auto-refresh useEffect with modal checking
  useEffect(() => {
    // Update ref when modal state changes
    isModalOpenRef.current = detailsModal.visible || dataViewModal.visible || actionModal.visible || historyModal.visible || bulkApproveModal.visible;
    
    if (autoRefreshEnabled) {
      setRefreshCountdown(refreshInterval);

      if (countdownRef.current) clearInterval(countdownRef.current);
      countdownRef.current = setInterval(() => {
        setRefreshCountdown(prev => {
          if (prev <= 1) {
            return refreshInterval;
          }
          return prev - 1;
        });
      }, 1000);

      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        // CRITICAL: Check if any modal is open using the ref
        if (isModalOpenRef.current) {
          console.log('⏸️ Auto-refresh skipped - modal is open');
          setLastRefreshTime(new Date());
          return;
        }
        
        console.log(`🔄 Auto-refreshing data... (${new Date().toLocaleTimeString()})`);
        loadData(true);
        setLastRefreshTime(new Date());
      }, refreshInterval * 1000);

    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      setRefreshCountdown(0);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoRefreshEnabled, refreshInterval]);

  // FIXED: loadData function
  const loadData = async (silent = false) => {
    if (!checkSession(navigate)) return;
    
    // CRITICAL: If this is auto-refresh and a modal is open, skip entirely
    if (silent && isModalOpenRef.current) {
      console.log('⏸️ Auto-refresh skipped - modal is open (in loadData)');
      return;
    }
    
    try {
      if (!silent) setLoading(true);
      console.log('📋 Loading workflow instances...');

      const response = await api.get('/workflow/instances');

      if (response.data.success) {
        setInstances(response.data.data || []);
        setFilteredInstances(response.data.data || []);
        if (!silent) {
          console.log(`✅ Loaded ${response.data.data.length} instances`);
        }
      } else {
        if (!silent) {
          message.error('Failed to load data: ' + (response.data.message || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Failed to load workflow instances:', error);
      if (!silent) {
        if (error.response?.status === 401) {
          message.error('Session expired. Please login again.');
          navigate('/login');
        } else if (error.response?.status === 403) {
          message.error(error.response?.data?.message || 'Access denied. You do not have permission to view these forms.');
        } else {
          message.error('Failed to load data: ' + (error.response?.data?.message || error.message));
        }
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const refreshSubmissionDetails = async () => {
    if (!detailsModal.instance) return;

    console.log('🔄 Refreshing submission details...');
    
    if (detailsModal.instance.form_id && currentFormId !== detailsModal.instance.form_id) {
      await fetchFieldOrder(detailsModal.instance.form_id);
    }
    
    try {
      const response = await api.get('/workflow/form-submissions', {
        params: {
          form_id: detailsModal.instance.form_id,
          workflow_instance_id: detailsModal.instance.id
        }
      });

      const result = response.data.data;
      let allSubmissions = result.submissions || [];
      const tableColumns = result.table_columns || [];

      const isDMADeskUser = result.workflow_info?.is_dma_desk_user || userInfo.roleType === 'DMA_DESK_USER';

      const columnLabelMap = new Map();
      if (formFieldOrder && formFieldOrder.length > 0) {
        formFieldOrder.forEach(field => {
          if (field.key && field.label) {
            columnLabelMap.set(field.key, field.label);
          }
        });
        console.log('📋 Column label map for table:', Array.from(columnLabelMap.entries()));
      }

      allSubmissions = allSubmissions.sort((a, b) => {
        const divisionA = (a.user_division_name || a.division_name || a.user_division || '').toLowerCase();
        const divisionB = (b.user_division_name || b.division_name || b.user_division || '').toLowerCase();
        
        if (divisionA < divisionB) return -1;
        if (divisionA > divisionB) return 1;
        
        const districtA = (a.user_district_name || a.district_name || a.user_district || '').toLowerCase();
        const districtB = (b.user_district_name || b.district_name || b.user_district || '').toLowerCase();
        
        if (districtA < districtB) return -1;
        if (districtA > districtB) return 1;
        
        const ulbA = (a.ulb_name || a.user_ulb_name || '').toLowerCase();
        const ulbB = (b.ulb_name || b.user_ulb_name || '').toLowerCase();
        
        if (ulbA < ulbB) return -1;
        if (ulbA > ulbB) return 1;
        
        return 0;
      });

      extractFilterOptions(allSubmissions);
      const stageInfo = getWorkflowStageInfo(detailsModal.instance);

      let canTakeAction = false;

      if (isDMADeskUser) {
        canTakeAction = false;
        console.log(`🔍 DMA Desk User - view only mode, no action buttons`);
      } else if (userInfo.roleType === 'ADMIN') {
        canTakeAction = true;
      } else if (userInfo.roleType === 'DMA_DESK_OFFICER') {
        const hasSubmissionsAtDMADesk = allSubmissions.some(sub => {
          return sub.current_stage === 'DMA_DESK_OFFICER' &&
                 sub.user_id !== userInfo.id &&
                 (sub.workflow_status !== 'approved');
        });
        canTakeAction = hasSubmissionsAtDMADesk;
        console.log(`🔍 DMA Desk Officer canTakeAction: ${canTakeAction}, deskIds: ${userInfo.deskIds.join(', ')}`);
      }
      else if (userInfo.roleType === 'DAO') {
        const hasSubmissionInDistrict = allSubmissions.some(sub => {
          const subDistrict = sub.user_district || sub.district_id;
          return subDistrict && String(subDistrict) === String(userInfo.district) && sub.user_id !== userInfo.id;
        });
        canTakeAction = hasSubmissionInDistrict;
      }
      else if (userInfo.roleType === 'DIVISIONAL_OFFICER') {
        const hasSubmissionInDivision = allSubmissions.some(sub => {
          const subDivision = sub.user_division || sub.division_id;
          return subDivision && String(subDivision) === String(userInfo.division) && sub.user_id !== userInfo.id;
        });
        canTakeAction = hasSubmissionInDivision;
      }
      else {
        const permissionResult = checkUserPermission(detailsModal.instance, userInfo.roleType, userInfo.district, userInfo.division);
        canTakeAction = permissionResult.canTakeAction;
      }

      const fixedColumns = [];

      fixedColumns.push({
        title: 'Status',
        dataIndex: 'workflow_status',
        key: 'workflow_status',
        width: 80,
        fixed: 'left',
        render: (status, record) => {
          if (isDraftSubmission(record)) {
            return (
              <Tag color="default" icon={<FileDoneOutlined />}>
                DRAFT
              </Tag>
            );
          }

          const colors = {
            'pending': 'orange',
            'in_progress': 'processing',
            'approved': 'success',
            'rejected': 'error',
            'escalated': 'red'
          };
          return (
            <Tag color={colors[status] || 'default'}>
              {status?.replace('_', ' ').toUpperCase() || 'PENDING'}
            </Tag>
          );
        }
      });

      const locationColumns = [];

      if (tableColumns.includes('division_id') || allSubmissions.some(s => s.user_division_name || s.division_name)) {
        locationColumns.push({
          title: 'Division',
          key: 'division_info',
          width: 120,
          fixed: 'left',
          render: (_, record) => {
            const divisionName = record.user_division_name || record.division_name || record.user_division;
            if (divisionName && divisionName !== 'null' && divisionName !== 'undefined') {
              return (
                <Space>
                  <ApartmentOutlined style={{ color: '#faad14' }} />
                  <span>{divisionName}</span>
                </Space>
              );
            }
            if (record.division_id) {
              return (
                <Space>
                  <ApartmentOutlined style={{ color: '#faad14' }} />
                  <span>Division ID: {record.division_id}</span>
                </Space>
              );
            }
            return '-';
          }
        });
      }

      if (tableColumns.includes('district_id') || allSubmissions.some(s => s.user_district_name || s.district_name)) {
        locationColumns.push({
          title: 'District',
          key: 'district_info',
          width: 120,
          fixed: 'left',
          render: (_, record) => {
            const districtName = record.user_district_name || record.district_name || record.user_district;
            if (districtName && districtName !== 'null' && districtName !== 'undefined') {
              return (
                <Space>
                  <EnvironmentOutlined style={{ color: '#52c41a' }} />
                  <span>{districtName}</span>
                </Space>
              );
            }
            if (record.district_id) {
              return (
                <Space>
                  <span>District ID: {record.district_id}</span>
                </Space>
              );
            }
            return '-';
          }
        });
      }

      if (tableColumns.includes('ulb_id') || allSubmissions.some(s => s.ulb_name)) {
        locationColumns.push({
          title: 'ULB',
          key: 'ulb_info',
          width: 150,
          fixed: 'left',
          render: (_, record) => {
            const ulbName = record.ulb_name || record.ulb_name_display || record.user_ulb_name;
            if (ulbName && ulbName !== 'null' && ulbName !== 'undefined') {
              return (
                <Space>
                  <BankOutlined style={{ color: '#1890ff' }} />
                  <span style={{ fontWeight: 500 }}>{ulbName}</span>
                </Space>
              );
            }
            if (record.ulb_id) {
              return (
                <Space>
                  <span>ULB ID: {record.ulb_id}</span>
                </Space>
              );
            }
            return '-';
          }
        });
      }

      const dynamicColumns = tableColumns
        .filter(col => {
          if (EXCLUDED_TABLE_FIELDS.has(col)) {
            return false;
          }
          
          const colLower = col.toLowerCase();
          if (colLower.includes('_at') && (colLower.includes('created') || colLower.includes('updated'))) {
            return false;
          }
          
          return true;
        })
        .map(col => {
          let displayTitle = columnLabelMap.get(col);
          
          if (!displayTitle) {
            displayTitle = col
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase())
              .replace(/No Of/gi, 'No. of')
              .replace(/UlB/gi, 'ULB')
              .replace(/Upscs/gi, 'UPHCs');
          }
          
          return {
            title: displayTitle,
            dataIndex: col,
            key: col,
            ellipsis: true,
            render: (text) => {
              if (text === null || text === undefined) return '-';
              if (typeof text === 'object') return JSON.stringify(text).substring(0, 50);
              if (typeof text === 'boolean') return text ? 'Yes' : 'No';
              if (typeof text === 'number' || (!isNaN(parseFloat(text)) && isFinite(text))) {
                const num = parseFloat(text);
                if (!isNaN(num)) {
                  if (Number.isInteger(num)) {
                    return num.toString();
                  } else {
                    return parseFloat(num.toFixed(2)).toString();
                  }
                }
              }
              return String(text);
            }
          };
        });

      const actionsColumn = {
        title: 'Actions',
        key: 'actions',
        width: 320,
        fixed: 'right',
        render: (_, submissionRecord) => {
          const canTakeActionOnSubmission = checkSubmissionPermission(submissionRecord);
          const isCompleted = submissionRecord.workflow_status === 'approved';
          const isRejected = submissionRecord.workflow_status === 'rejected';
          const isDraft = isDraftSubmission(submissionRecord);
          const isDMADeskUserRole = userInfo.roleType === 'DMA_DESK_USER' || isDMADeskUser;

          return (
            <Space size="small" wrap>
              <Tooltip title="View complete data">
                <Button
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={() => viewRowData(submissionRecord)}
                >
                  View Data
                </Button>
              </Tooltip>

              {!isDMADeskUserRole && !isDraft && !isCompleted && canTakeActionOnSubmission && canTakeAction && (
                <>
                  <Tooltip title={isRejected ? "Review this rejected submission" : "Approve this submission"}>
                    <Button
                      type="primary"
                      icon={isRejected ? <RedoOutlined /> : <CheckOutlined />}
                      size="small"
                      onClick={() => openActionModal(submissionRecord, isRejected ? 'reapprove' : 'approve')}
                      style={{ background: isRejected ? '#14d0fa' : '#52c41a', borderColor: isRejected ? '#faad14' : '#52c41a' }}
                    >
                      {isRejected ? 'Review' : 'Approve'}
                    </Button>
                  </Tooltip>

                  {!isRejected && (
                    <Tooltip title="Reject this submission">
                      <Button
                        danger
                        type="primary"
                        icon={<CloseOutlined />}
                        size="small"
                        onClick={() => openActionModal(submissionRecord, 'reject')}
                      />
                    </Tooltip>
                  )}

                  {!isRejected && (
                    <Tooltip title="Escalate (SLA breach)">
                      <Button
                        type="default"
                        icon={<ArrowUpOutlined />}
                        size="small"
                        onClick={() => openActionModal(submissionRecord, 'escalate')}
                        style={{ color: '#faad14', borderColor: '#faad14' }}
                      />
                    </Tooltip>
                  )}
                </>
              )}

              <Tooltip title="View Action History">
                <Button
                  icon={<HistoryOutlined />}
                  size="small"
                  onClick={() => fetchActionHistory(submissionRecord.id)}
                >
                  History
                </Button>
              </Tooltip>

              {isDraft && !isCompleted && !isRejected && (
                <Tag color="default" icon={<FileDoneOutlined />}>Draft</Tag>
              )}
              {isRejected && (
                <Tag color="error" icon={<CloseOutlined />}>Rejected</Tag>
              )}
              {isCompleted && (
                <Tag color="success" icon={<CheckOutlined />}>Approved</Tag>
              )}
              {!canTakeActionOnSubmission && !isCompleted && !isDraft && !isRejected && !isDMADeskUserRole && (
                <Tag color="default">Read Only</Tag>
              )}
              {isDMADeskUserRole && (
                <Tag color="blue" icon={<EyeOutlined />}>View Only</Tag>
              )}
            </Space>
          );
        },
      };

      const allColumns = [
        ...fixedColumns,
        ...locationColumns,
        ...dynamicColumns,
        actionsColumn
      ];

      const filteredData = filterSubmissions(allSubmissions, submissionFilters);

      setDetailsModal(prev => ({
        ...prev,
        loading: false,
        allSubmissions: allSubmissions,
        tableData: filteredData,
        tableColumns: allColumns,
        canTakeAction: canTakeAction,
        stageInfo: stageInfo,
        selectedRowKeys: [],
        isDMADeskUser: isDMADeskUser
      }));

    } catch (error) {
      console.error('❌ Failed to refresh submission details:', error);
      if (error.response?.status === 403) {
        message.error(error.response?.data?.message || 'Access denied to this form');
        setDetailsModal({ visible: false, loading: false, instance: null, submissionData: null, allSubmissions: [], tableColumns: [], tableData: [], selectedRowKeys: [], workflowData: null, canTakeAction: false, currentStage: null, stageInfo: null, isDMADeskUser: false });
      } else {
        message.error('Failed to refresh submission details');
      }
    }
  };

  const refreshData = () => {
    loadData(false);
    setLastRefreshTime(new Date());
    message.success('Data refreshed');
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    filterInstances(value);
  };

  const filterInstances = (searchValue) => {
    if (!searchValue.trim()) {
      setFilteredInstances(instances);
      return;
    }

    const searchLower = searchValue.toLowerCase().trim();
    const filtered = instances.filter(instance => {
      const formNameMatch = instance.form_name && instance.form_name.toLowerCase().includes(searchLower);
      const formIdMatch = instance.form_id && instance.form_id.toString().includes(searchLower);
      const instanceIdMatch = instance.id && instance.id.toString().includes(searchLower);
      const statusMatch = instance.status && instance.status.toLowerCase().includes(searchLower);
      const roleMatch = instance.created_by_role && instance.created_by_role.toLowerCase().includes(searchLower);

      return formNameMatch || formIdMatch || instanceIdMatch || statusMatch || roleMatch;
    });

    setFilteredInstances(filtered);
  };

  const checkUserPermission = (instance, userRole, userDistrict, userDivision) => {
    if (userInfo.roleType === 'DMA_DESK_USER') {
      return { canTakeAction: false, reason: "❌ DMA Desk Users have view-only access" };
    }

    if (instance.created_by === userInfo.id) {
      return { canTakeAction: false, reason: "❌ Cannot approve your own submission" };
    }

    if (userRole === 'ADMIN') {
      if (instance.status !== 'approved' && instance.status !== 'rejected') {
        return { canTakeAction: true, reason: "✅ Admin approval" };
      }
      return { canTakeAction: false, reason: "❌ Instance already completed" };
    }

    if (userRole === 'DMA_DESK_OFFICER') {
      if (instance.status !== 'approved' && instance.status !== 'rejected') {
        return { canTakeAction: true, reason: "✅ DMA Desk Officer approval (desk-based access)" };
      }
      return { canTakeAction: false, reason: "❌ Instance already completed" };
    }

    let workflowSequence = ['ULB', 'DISTRICT_ADMIN', 'DIVISIONAL_OFFICER', 'DMA_DESK_OFFICER'];
    if (instance.workflow_sequence) {
      try {
        workflowSequence = JSON.parse(instance.workflow_sequence);
      } catch (e) {
        console.error('Failed to parse workflow sequence:', e);
      }
    }

    const currentStageIndex = instance.current_stage_index || 0;
    const currentStageName = workflowSequence[currentStageIndex];

    if (userRole === 'DIVISIONAL_OFFICER' && currentStageName === 'DIVISIONAL_OFFICER') {
      if (instance.division === userDivision || String(instance.division) === String(userDivision)) {
        return { canTakeAction: true, reason: "✅ Divisional Officer approval" };
      } else {
        return { canTakeAction: false, reason: `❌ Submission not in your division` };
      }
    }

    if ((userRole === 'DAO' || userRole === 'DISTRICT_ADMIN') && currentStageName === 'DISTRICT_ADMIN') {
      if (instance.district === userDistrict || String(instance.district) === String(userDistrict)) {
        return { canTakeAction: true, reason: "✅ District Admin approval" };
      } else {
        return { canTakeAction: false, reason: `❌ Submission not in your district` };
      }
    }

    if (userRole === 'ULB' && currentStageName === 'ULB') {
      return { canTakeAction: false, reason: "❌ ULB users cannot approve submissions" };
    }

    return {
      canTakeAction: false,
      reason: `❌ Not your turn. Current stage: ${currentStageName}, Your role: ${userRole}`
    };
  };

  const getWorkflowStageInfo = (instance) => {
    let workflowSequence = ['ULB', 'DISTRICT_ADMIN', 'DIVISIONAL_OFFICER', 'DMA_DESK_OFFICER'];
    if (instance.workflow_sequence) {
      try {
        workflowSequence = JSON.parse(instance.workflow_sequence);
      } catch (e) {
        console.error('Failed to parse workflow sequence:', e);
      }
    }

    const currentIndex = instance.current_stage_index || 0;
    const currentStage = workflowSequence[currentIndex];
    const totalStages = workflowSequence.length;
    const progress = ((currentIndex) / totalStages) * 100;

    return {
      workflowSequence,
      currentIndex,
      totalStages,
      currentStage,
      progress,
      isCompleted: instance.status === 'approved',
      isRejected: instance.status === 'rejected',
      isEscalated: instance.status === 'escalated'
    };
  };

  // FIXED: viewRowData with proper state management and refs
const viewRowData = async (record) => {
  // Store the record in ref for persistence
  dataViewRecordRef.current = record;
  
  let formId = record.form_id || record.formId || currentViewFormId;
  
  if (!formId && detailsModal.instance) {
    formId = detailsModal.instance.form_id;
  }
  
  console.log('📋 Viewing data for submission:', { 
    recordId: record.id, 
    formId,
    recordKeys: Object.keys(record)
  });
  
  // Open modal with record - store the record data
  setDataViewModal({ 
    visible: true, 
    record: record, 
    loading: true 
  });
  
  try {
    if (formId) {
      console.log('⏳ Fetching field order for form:', formId);
      
      // Check if we already have this form's field order cached
      if (fieldOrderCacheRef.current[formId]) {
        console.log('📋 Using cached field order from ref');
        setFormFieldOrder(fieldOrderCacheRef.current[formId]);
        setCurrentFormId(formId);
        // Turn off loading, keep the record
        setDataViewModal(prev => ({
          ...prev,
          loading: false
        }));
        return;
      }
      
      // Fetch field order
      await fetchFieldOrder(formId);
      
      // Cache the result
      if (formFieldOrder.length > 0) {
        fieldOrderCacheRef.current[formId] = formFieldOrder;
      }
      
    } else if (record.id) {
      console.log('Attempting to fetch form_id from submission API...');
      try {
        const response = await api.get(`/submissions/${record.id}`);
        if (response.data.success && response.data.data.form_id) {
          formId = response.data.data.form_id;
          console.log('✅ Retrieved form_id from submission API:', formId);
          await fetchFieldOrder(formId);
          if (formFieldOrder.length > 0) {
            fieldOrderCacheRef.current[formId] = formFieldOrder;
          }
        }
      } catch (error) {
        console.error('Failed to fetch form_id from submission API:', error);
      }
    }
    
    // IMPORTANT: Preserve the record when turning off loading
    setDataViewModal(prev => ({
      ...prev,
      loading: false,
      record: prev.record || dataViewRecordRef.current
    }));
    
  } catch (error) {
    console.error('❌ Error loading field order:', error);
    setDataViewModal(prev => ({
      ...prev,
      loading: false,
      record: prev.record || dataViewRecordRef.current
    }));
  }
};

  // UPDATED: fetchFieldOrder with better caching
const fetchFieldOrder = async (formId) => {
  if (!formId) {
    console.warn('⚠️ Cannot fetch field order - no form ID provided');
    setFormFieldOrder([]);
    return [];
  }
  
  // If we already have the data for this form, use it
  if (currentFormId === formId && formFieldOrder.length > 0) {
    console.log('📋 Using cached field order for form', formId);
    return formFieldOrder;
  }
  
  try {
    setFieldOrderLoading(true);
    console.log(`📋 Fetching field order for form ${formId}`);
    
    const response = await api.get(`/workflow/form-field-order/${formId}`);
    
    console.log('📋 Field order API response:', response.data);
    
    if (response.data.success && response.data.data && response.data.data.length > 0) {
      console.log('✅ Field order data:', response.data.data);
      console.log('✅ Number of fields in schema:', response.data.data.length);
      
      response.data.data.forEach((field, idx) => {
        console.log(`  ${idx}: ${field.key} (position: ${field.position}) - Label: "${field.label}"`);
      });
      
      // Update state
      setFormFieldOrder(response.data.data);
      setCurrentFormId(formId);
      
      // Also cache in ref for immediate access
      fieldOrderCacheRef.current[formId] = response.data.data;
      
      return response.data.data;
    } else {
      console.log(`⚠️ No field order found for form ${formId}`);
      setFormFieldOrder([]);
      return [];
    }
  } catch (error) {
    console.error(`❌ Failed to fetch field order for form ${formId}:`, error);
    setFormFieldOrder([]);
    return [];
  } finally {
    setFieldOrderLoading(false);
  }
};

const renderVerticalData = (record) => {
  if (!record) return null;

  // If field order is empty, show fallback with data
  if (formFieldOrder.length === 0 && !fieldOrderLoading) {
    console.log('⚠️ No field order available - showing fallback view');
    const dataFields = Object.keys(record).filter(key => 
      ![
        'id', 'workflow_status', 'user_id', 'created_at', 'updated_at',
        'status', 'actions', 'permissions', 'labeled_data', 'raw_data',
        'current_stage_index', 'is_dma_desk_user', 'form_id',
        'user_name', 'user_role', 'district_id', 'division_id', 'ulb_id',
        'user_district_name', 'user_division_name', 'ulb_name'
      ].includes(key) && 
      record[key] !== null && 
      record[key] !== undefined && 
      record[key] !== ''
    );
    
    if (dataFields.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Alert 
            message="No Data Available" 
            description="This submission has no data fields to display."
            type="info"
            showIcon
          />
        </div>
      );
    }
    
    return (
      <div>
        <Alert 
          message="Loading Field Structure..." 
          description="Please wait while we load the correct field labels."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Descriptions column={1} bordered size="small" title="Data (Loading Labels...)">
          {dataFields.map(key => {
            const value = record[key];
            const displayLabel = key
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase())
              .replace(/No Of/gi, 'No. of')
              .replace(/UlB/gi, 'ULB');
            
            if (typeof value === 'object') {
              return (
                <Descriptions.Item key={key} label={displayLabel}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </Descriptions.Item>
              );
            }
            return (
              <Descriptions.Item key={key} label={displayLabel}>
                {String(value)}
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      </div>
    );
  }

  console.log('📊 Rendering vertical data for record:', Object.keys(record));
  console.log('📊 Available field order:', formFieldOrder.map(f => ({ 
    key: f.key, 
    position: f.position, 
    label: f.label,
    type: f.type,
    parent_id: f.parent_id
  })));

  const fieldByKeyMap = new Map();
  const fieldByIdMap = new Map();
  const childrenByParentIdMap = new Map();
  const headingFields = new Set();
  
  if (formFieldOrder && formFieldOrder.length > 0) {
    formFieldOrder.forEach((field) => {
      fieldByKeyMap.set(field.key, field);
      fieldByIdMap.set(field.field_id, field);
      
      if (field.type === 'heading') {
        headingFields.add(field.key);
      }
      
      if (field.parent_id) {
        if (!childrenByParentIdMap.has(field.parent_id)) {
          childrenByParentIdMap.set(field.parent_id, []);
        }
        childrenByParentIdMap.get(field.parent_id).push(field);
      }
    });
    
    for (const [parentId, children] of childrenByParentIdMap.entries()) {
      children.sort((a, b) => (a.position || 0) - (b.position || 0));
    }
  }

  const formatFieldValue = (fieldKey, value) => {
    if (value === null || value === undefined) return null;
    
    if (typeof value === 'object') {
      return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {JSON.stringify(value, null, 2)}
      </pre>;
    }
    
    if (typeof value === 'boolean') {
      return value ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>;
    }
    
    if (fieldKey.includes('_at') && typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    }
    
    if (typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value))) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        if (Number.isInteger(num)) {
          return num.toString();
        } else {
          return parseFloat(num.toFixed(2)).toString();
        }
      }
    }
    
    if (typeof value === 'string' && !isNaN(parseFloat(value)) && isFinite(value)) {
      const num = parseFloat(value);
      if (Number.isInteger(num)) {
        return num.toString();
      } else {
        return parseFloat(num.toFixed(2)).toString();
      }
    }
    
    return value;
  };

  const getFieldLabel = (fieldKey) => {
    const fieldObj = fieldByKeyMap.get(fieldKey);
    if (fieldObj?.label) {
      return fieldObj.label;
    }
    
    if (fieldKey === 'current_stage') return 'Current Stage';
    if (fieldKey === 'workflow_status') return 'Workflow Status';
    if (fieldKey === 'user_district_name') return 'District';
    if (fieldKey === 'user_division_name') return 'Division';
    if (fieldKey === 'ulb_name') return 'ULB';
    
    return fieldKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/No Of/gi, 'No. of')
      .replace(/UlB/gi, 'ULB');
  };

  // CRITICAL: Get all fields that are NOT heading fields
  const nonHeadingFields = formFieldOrder.filter(f => f.type !== 'heading');
  
  // Get heading fields
  const headingFieldsList = formFieldOrder
    .filter(f => f.type === 'heading')
    .sort((a, b) => (a.position || 0) - (b.position || 0));

  // Build ordered list of non-heading fields
  const orderedFieldKeys = [];
  const processedKeys = new Set();
  
  const addFieldWithChildrenInOrder = (fieldKey) => {
    if (processedKeys.has(fieldKey)) return;
    processedKeys.add(fieldKey);
    
    const fieldObj = fieldByKeyMap.get(fieldKey);
    
    // Skip heading fields - they are rendered separately
    if (fieldObj?.type === 'heading') {
      // Process children of heading
      if (fieldObj.field_id && childrenByParentIdMap.has(fieldObj.field_id)) {
        const children = childrenByParentIdMap.get(fieldObj.field_id);
        children.sort((a, b) => (a.position || 0) - (b.position || 0));
        for (const child of children) {
          addFieldWithChildrenInOrder(child.key);
        }
      }
      return;
    }
    
    orderedFieldKeys.push(fieldKey);
    
    if (fieldObj?.field_id && childrenByParentIdMap.has(fieldObj.field_id)) {
      const children = childrenByParentIdMap.get(fieldObj.field_id);
      children.sort((a, b) => (a.position || 0) - (b.position || 0));
      for (const child of children) {
        addFieldWithChildrenInOrder(child.key);
      }
    }
  };

  // Start with top-level non-heading fields
  const topLevelFields = nonHeadingFields
    .filter(f => !f.parent_id)
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  
  for (const field of topLevelFields) {
    addFieldWithChildrenInOrder(field.key);
  }

  console.log('📋 Ordered field keys (excluding headings):', orderedFieldKeys);
  console.log('📋 Heading fields:', headingFieldsList.map(f => f.label));

  return (
    <Descriptions column={1} bordered size="small" className="vertical-data-descriptions">
      {/* Location fields - always show first */}
      {record.district_name && record.district_name !== 'null' && record.district_name !== 'undefined' && (
        <Descriptions.Item label="District">
          <Space><EnvironmentOutlined style={{ color: '#52C41A' }} />{record.district_name}</Space>
        </Descriptions.Item>
      )}
      {record.division_name && record.division_name !== 'null' && record.division_name !== 'undefined' && (
        <Descriptions.Item label="Division">
          <Space><ApartmentOutlined style={{ color: '#FAAD14' }} />{record.division_name}</Space>
        </Descriptions.Item>
      )}
      {record.ulb_name && record.ulb_name !== 'null' && record.ulb_name !== 'undefined' && (
        <Descriptions.Item label="ULB">
          <Space><BankOutlined style={{ color: '#1890FF' }} />{record.ulb_name}</Space>
        </Descriptions.Item>
      )}
      
      {/* Current Stage and Workflow Status */}
      {record.current_stage !== undefined && record.current_stage !== null && (
        <Descriptions.Item label="Current Stage">
          <Tag color="processing">{record.current_stage}</Tag>
        </Descriptions.Item>
      )}
      {record.workflow_status !== undefined && record.workflow_status !== null && (
        <Descriptions.Item label="Workflow Status">
          <Tag color={
            record.workflow_status === 'approved' ? 'success' :
            record.workflow_status === 'rejected' ? 'error' :
            record.workflow_status === 'in_progress' ? 'processing' :
            record.workflow_status === 'escalated' ? 'red' : 'default'
          }>
            {record.workflow_status?.replace('_', ' ').toUpperCase()}
          </Tag>
        </Descriptions.Item>
      )}
      
      {/* Render headings with their children */}
      {headingFieldsList.map(headingField => {
        const children = childrenByParentIdMap.get(headingField.field_id) || [];
        const sortedChildren = children.sort((a, b) => (a.position || 0) - (b.position || 0));
        
        // Check if any child has actual data
        const hasDataChildren = sortedChildren.some(child => {
          const value = record[child.key];
          return value !== null && value !== undefined && value !== '' && value !== child.key;
        });
        
        if (!hasDataChildren) return null;
        
        return (
          <React.Fragment key={headingField.key}>
            {/* Render the heading as a section header */}
            <Descriptions.Item 
              label={headingField.label}
              style={{ backgroundColor: '#fafafa' }}
            >
              <strong style={{ 
                fontSize: '16px', 
                display: 'block', 
                marginTop: '8px', 
                marginBottom: '4px',
                color: '#2c3e50'
              }}>
                {headingField.label}
              </strong>
            </Descriptions.Item>
            
            {/* Render children of this heading */}
            {sortedChildren.map(child => {
              const childValue = record[child.key];
              
              // Skip if no value or value is the same as the key (no actual data)
              if (childValue === null || childValue === undefined) return null;
              if (childValue === '' || childValue === child.key) return null;
              
              const displayValue = formatFieldValue(child.key, childValue);
              if (displayValue === null) return null;
              
              return (
                <Descriptions.Item 
                  key={child.key} 
                  label={child.label || child.key}
                  style={{ paddingLeft: '20px' }}
                >
                  {displayValue}
                </Descriptions.Item>
              );
            })}
          </React.Fragment>
        );
      })}
      
      {/* Render remaining fields that don't belong to any heading */}
      {orderedFieldKeys.map(fieldKey => {
        const fieldObj = fieldByKeyMap.get(fieldKey);
        const parentId = fieldObj?.parent_id;
        
        // Skip if this field is already rendered under a heading
        if (parentId && headingFieldsList.some(h => h.field_id === parentId)) {
          return null;
        }
        
        const value = record[fieldKey];
        if (value === null || value === undefined || value === '' || value === fieldKey) return null;
        
        const displayValue = formatFieldValue(fieldKey, value);
        if (displayValue === null) return null;
        
        const label = getFieldLabel(fieldKey);
        
        let indentLevel = 0;
        if (parentId) {
          let currentParentId = parentId;
          while (currentParentId) {
            indentLevel++;
            const parentField = fieldByIdMap.get(currentParentId);
            currentParentId = parentField?.parent_id;
          }
        }
        
        return (
          <Descriptions.Item 
            key={fieldKey} 
            label={label} 
            style={{ paddingLeft: `${indentLevel * 20}px` }}
          >
            {displayValue}
          </Descriptions.Item>
        );
      })}
      
      {/* Created At and Updated At */}
      {record.created_at && (
        <Descriptions.Item label="Created At">
          <ClockCircleOutlined style={{ marginRight: 8 }} />
          {new Date(record.created_at).toLocaleString()}
        </Descriptions.Item>
      )}
      
      {record.updated_at && record.updated_at !== record.created_at && (
        <Descriptions.Item label="Last Updated">
          <FieldTimeOutlined style={{ marginRight: 8 }} />
          {new Date(record.updated_at).toLocaleString()}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

  const fetchActionHistory = async (submissionId) => {
    try {
      setHistoryModal({
        visible: true,
        loading: true,
        submissionId: submissionId,
        actions: []
      });

      console.log(`📜 Fetching action history for submission: ${submissionId}`);

      const response = await api.get(`/workflow/submission-actions/${submissionId}`);

      console.log('📜 Action history response:', response.data);

      if (response.data.success) {
        const actions = response.data.data || [];

        const formattedActions = actions.map(action => ({
          ...action,
          performed_by_name: action.performed_by_user_name || action.performed_by_name || action.performed_by_name_display || `User ${action.performed_by}`,
          created_at: action.created_at,
          action: action.action,
          performed_by_role: action.performed_by_role,
          comments: action.comments,
          stage_name: action.stage_name,
          submission_id: action.submission_id
        }));

        setHistoryModal({
          visible: true,
          submissionId: submissionId,
          actions: formattedActions,
          loading: false
        });
        console.log(`✅ Loaded ${formattedActions.length} actions for submission ${submissionId}`);
      } else {
        message.error('Failed to load action history: ' + (response.data.message || 'Unknown error'));
        setHistoryModal({ visible: false, submissionId: null, actions: [], loading: false });
      }
    } catch (error) {
      console.error('Failed to load action history:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load action history';
      message.error(errorMessage);
      setHistoryModal({ visible: false, submissionId: null, actions: [], loading: false });
    }
  };

  const checkSubmissionPermission = (submission) => {
    if (userInfo.roleType === 'DMA_DESK_USER') {
      console.log(`❌ DMA Desk User - view only, no action buttons for submission ${submission.id}`);
      return false;
    }

    console.log('🔍 Checking permission for submission:', {
      submissionId: submission.id,
      submissionUserId: submission.user_id,
      submissionDistrict: submission.user_district,
      submissionDivision: submission.user_division,
      submissionRole: submission.user_role,
      currentUserId: userInfo.id,
      userRole: userInfo.roleType,
      userDistrict: userInfo.district,
      userDivision: userInfo.division,
      userDeskIds: userInfo.deskIds,
      workflowStatus: submission.workflow_status,
      currentStage: submission.current_stage,
      status: submission.status
    });

    if (isDraftSubmission(submission)) {
      console.log(`❌ Cannot take action on draft submission ${submission.id}`);
      return false;
    }

    if (submission.user_id === userInfo.id) {
      console.log(`❌ Cannot approve own submission`);
      return false;
    }

    const isRejected = submission.workflow_status === 'rejected';

    if (userInfo.roleType === 'ADMIN') {
      if (submission.workflow_status !== 'approved') {
        console.log(`✅ Admin ${isRejected ? 'review' : 'approval'} allowed for submission ${submission.id}`);
        return true;
      }
      console.log(`❌ Submission already approved`);
      return false;
    }

    if (userInfo.roleType === 'DMA_DESK_OFFICER') {
      if (submission.current_stage !== 'DMA_DESK_OFFICER') {
        console.log(`❌ DMA Desk Officer - submission not at DMA stage. Current stage: ${submission.current_stage}`);
        return false;
      }

      if (submission.workflow_status === 'approved') {
        console.log(`❌ Submission already approved`);
        return false;
      }

      console.log(`✅ DMA Desk Officer can act on submission at DMA stage (Desk IDs: ${userInfo.deskIds.join(', ')})`);
      return true;
    }

    const currentStage = submission.current_stage;

    if (isRejected) {
      if (userInfo.roleType === 'DAO' && currentStage === 'DISTRICT_ADMIN') {
        const submissionDist = submission.user_district || submission.district_id;
        const userDist = userInfo.district;

        if (submissionDist && userDist && String(submissionDist) === String(userDist)) {
          console.log(`✅ District Admin re-approval allowed - same district`);
          return true;
        }
      }

      if (userInfo.roleType === 'DIVISIONAL_OFFICER' && currentStage === 'DIVISIONAL_OFFICER') {
        const submissionDiv = submission.user_division || submission.division_id;
        const userDiv = userInfo.division;

        if (submissionDiv && userDiv && String(submissionDiv) === String(userDiv)) {
          console.log(`✅ Divisional Officer re-approval allowed - same division`);
          return true;
        }
      }

      console.log(`❌ No re-approval permission for submission ${submission.id}. Current stage: ${currentStage}, User role: ${userInfo.roleType}`);
      return false;
    }

    if (userInfo.roleType === 'DAO' && currentStage === 'DISTRICT_ADMIN') {
      const submissionDist = submission.user_district || submission.district_id;
      const userDist = userInfo.district;

      if (submissionDist && userDist && String(submissionDist) === String(userDist)) {
        console.log(`✅ District Admin approval allowed - same district`);
        return true;
      }
    }

    if (userInfo.roleType === 'DIVISIONAL_OFFICER' && currentStage === 'DIVISIONAL_OFFICER') {
      const submissionDiv = submission.user_division || submission.division_id;
      const userDiv = userInfo.division;

      if (submissionDiv && userDiv && String(submissionDiv) === String(userDiv)) {
        console.log(`✅ Divisional Officer approval allowed - same division`);
        return true;
      }
    }

    console.log(`❌ No permission for submission ${submission.id}. Current stage: ${currentStage}, User role: ${userInfo.roleType}`);
    return false;
  };

  const viewSubmissionDetails = async (record) => {
    setSubmissionsPageSize(10);
    setSubmissionsCurrentPage(1);
    setSubmissionFilters({ division: null, district: null, ulb: null });

    setCurrentViewFormId(record.form_id);
    
    if (record.form_id) {
      await fetchFieldOrder(record.form_id);
    }

    try {
      setDetailsModal({
        visible: true,
        loading: true,
        instance: record,
        submissionData: null,
        allSubmissions: [],
        tableColumns: [],
        tableData: [],
        selectedRowKeys: [],
        isDMADeskUser: false
      });

      console.log(`🔍 Fetching submissions for form_id: ${record.form_id}`);

      const response = await api.get('/workflow/form-submissions', {
        params: {
          form_id: record.form_id,
          workflow_instance_id: record.id
        }
      });

      const result = response.data.data;
      let allSubmissions = result.submissions || [];
      const tableColumns = result.table_columns || [];

      const isDMADeskUser = result.workflow_info?.is_dma_desk_user || userInfo.roleType === 'DMA_DESK_USER';

      const columnLabelMap = new Map();
      if (formFieldOrder && formFieldOrder.length > 0) {
        formFieldOrder.forEach(field => {
          if (field.key && field.label) {
            columnLabelMap.set(field.key, field.label);
          }
        });
        console.log('📋 Column label map for table:', Array.from(columnLabelMap.entries()));
      }

      allSubmissions = allSubmissions.sort((a, b) => {
        const divisionA = (a.user_division_name || a.division_name || a.user_division || '').toLowerCase();
        const divisionB = (b.user_division_name || b.division_name || b.user_division || '').toLowerCase();
        
        if (divisionA < divisionB) return -1;
        if (divisionA > divisionB) return 1;
        
        const districtA = (a.user_district_name || a.district_name || a.user_district || '').toLowerCase();
        const districtB = (b.user_district_name || b.district_name || b.user_district || '').toLowerCase();
        
        if (districtA < districtB) return -1;
        if (districtA > districtB) return 1;
        
        const ulbA = (a.ulb_name || a.user_ulb_name || '').toLowerCase();
        const ulbB = (b.ulb_name || b.user_ulb_name || '').toLowerCase();
        
        if (ulbA < ulbB) return -1;
        if (ulbA > ulbB) return 1;
        
        return 0;
      });

      extractFilterOptions(allSubmissions);
      const stageInfo = getWorkflowStageInfo(record);

      let canTakeAction = false;

      if (isDMADeskUser) {
        canTakeAction = false;
        console.log(`🔍 DMA Desk User - view only mode, no action buttons`);
      } else if (userInfo.roleType === 'ADMIN') {
        canTakeAction = true;
      } else if (userInfo.roleType === 'DMA_DESK_OFFICER') {
        const hasSubmissionsAtDMADesk = allSubmissions.some(sub => {
          return sub.current_stage === 'DMA_DESK_OFFICER' &&
                 sub.user_id !== userInfo.id &&
                 (sub.workflow_status !== 'approved');
        });
        canTakeAction = hasSubmissionsAtDMADesk;
        console.log(`🔍 DMA Desk Officer canTakeAction: ${canTakeAction}`);
      }
      else if (userInfo.roleType === 'DAO') {
        const hasSubmissionInDistrict = allSubmissions.some(sub => {
          const subDistrict = sub.user_district || sub.district_id;
          return subDistrict && String(subDistrict) === String(userInfo.district) && sub.user_id !== userInfo.id;
        });
        canTakeAction = hasSubmissionInDistrict;
      }
      else if (userInfo.roleType === 'DIVISIONAL_OFFICER') {
        const hasSubmissionInDivision = allSubmissions.some(sub => {
          const subDivision = sub.user_division || sub.division_id;
          return subDivision && String(subDivision) === String(userInfo.division) && sub.user_id !== userInfo.id;
        });
        canTakeAction = hasSubmissionInDivision;
      }
      else {
        const permissionResult = checkUserPermission(record, userInfo.roleType, userInfo.district, userInfo.division);
        canTakeAction = permissionResult.canTakeAction;
      }

      const fixedColumns = [];

      fixedColumns.push({
        title: 'Status',
        dataIndex: 'workflow_status',
        key: 'workflow_status',
        width: 80,
        fixed: 'left',
        render: (status, record) => {
          if (isDraftSubmission(record)) {
            return (
              <Tag color="default" icon={<FileDoneOutlined />}>
                DRAFT
              </Tag>
            );
          }

          const colors = {
            'pending': 'orange',
            'in_progress': 'processing',
            'approved': 'success',
            'rejected': 'error',
            'escalated': 'red'
          };
          return (
            <Tag color={colors[status] || 'default'}>
              {status?.replace('_', ' ').toUpperCase() || 'PENDING'}
            </Tag>
          );
        }
      });

      const locationColumns = [];

      if (tableColumns.includes('division_id') || allSubmissions.some(s => s.user_division_name || s.division_name)) {
        locationColumns.push({
          title: 'Division',
          key: 'division_info',
          width: 120,
          fixed: 'left',
          render: (_, record) => {
            const divisionName = record.user_division_name || record.division_name || record.user_division;
            if (divisionName && divisionName !== 'null' && divisionName !== 'undefined') {
              return (
                <Space>
                  <ApartmentOutlined style={{ color: '#faad14' }} />
                  <span>{divisionName}</span>
                </Space>
              );
            }
            if (record.division_id) {
              return (
                <Space>
                  <ApartmentOutlined style={{ color: '#faad14' }} />
                  <span>Division ID: {record.division_id}</span>
                </Space>
              );
            }
            return '-';
          }
        });
      }

      if (tableColumns.includes('district_id') || allSubmissions.some(s => s.user_district_name || s.district_name)) {
        locationColumns.push({
          title: 'District',
          key: 'district_info',
          width: 120,
          fixed: 'left',
          render: (_, record) => {
            const districtName = record.user_district_name || record.district_name || record.user_district;
            if (districtName && districtName !== 'null' && districtName !== 'undefined') {
              return (
                <Space>
                  <EnvironmentOutlined style={{ color: '#52c41a' }} />
                  <span>{districtName}</span>
                </Space>
              );
            }
            if (record.district_id) {
              return (
                <Space>
                  <span>District ID: {record.district_id}</span>
                </Space>
              );
            }
            return '-';
          }
        });
      }

      if (tableColumns.includes('ulb_id') || allSubmissions.some(s => s.ulb_name)) {
        locationColumns.push({
          title: 'ULB',
          key: 'ulb_info',
          width: 150,
          fixed: 'left',
          render: (_, record) => {
            const ulbName = record.ulb_name || record.ulb_name_display || record.user_ulb_name;
            if (ulbName && ulbName !== 'null' && ulbName !== 'undefined') {
              return (
                <Space>
                  <BankOutlined style={{ color: '#1890ff' }} />
                  <span style={{ fontWeight: 500 }}>{ulbName}</span>
                </Space>
              );
            }
            if (record.ulb_id) {
              return (
                <Space>
                  <span>ULB ID: {record.ulb_id}</span>
                </Space>
              );
            }
            return '-';
          }
        });
      }

      const dynamicColumns = tableColumns
        .filter(col => {
          if (EXCLUDED_TABLE_FIELDS.has(col)) {
            return false;
          }
          
          const colLower = col.toLowerCase();
          if (colLower.includes('_at') && (colLower.includes('created') || colLower.includes('updated'))) {
            return false;
          }
          
          return true;
        })
        .map(col => {
          let displayTitle = columnLabelMap.get(col);
          
          if (!displayTitle) {
            displayTitle = col
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase())
              .replace(/No Of/gi, 'No. of')
              .replace(/UlB/gi, 'ULB')
              .replace(/Upscs/gi, 'UPHCs');
          }
          
          return {
            title: displayTitle,
            dataIndex: col,
            key: col,
            ellipsis: true,
            render: (text) => {
              if (text === null || text === undefined) return '-';
              if (typeof text === 'object') return JSON.stringify(text).substring(0, 50);
              if (typeof text === 'boolean') return text ? 'Yes' : 'No';
              if (typeof text === 'number' || (!isNaN(parseFloat(text)) && isFinite(text))) {
                const num = parseFloat(text);
                if (!isNaN(num)) {
                  if (Number.isInteger(num)) {
                    return num.toString();
                  } else {
                    return parseFloat(num.toFixed(2)).toString();
                  }
                }
              }
              return String(text);
            }
          };
        });

      const actionsColumn = {
        title: 'Actions',
        key: 'actions',
        width: 320,
        fixed: 'right',
        render: (_, submissionRecord) => {
          const canTakeActionOnSubmission = checkSubmissionPermission(submissionRecord);
          const isCompleted = submissionRecord.workflow_status === 'approved';
          const isRejected = submissionRecord.workflow_status === 'rejected';
          const isDraft = isDraftSubmission(submissionRecord);
          const isDMADeskUserRole = userInfo.roleType === 'DMA_DESK_USER' || isDMADeskUser;

          return (
            <Space size="small" wrap>
              <Tooltip title="View complete data">
                <Button
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={() => viewRowData(submissionRecord)}
                >
                  View Data
                </Button>
              </Tooltip>

              {!isDMADeskUserRole && !isDraft && !isCompleted && canTakeActionOnSubmission && canTakeAction && (
                <>
                  <Tooltip title={isRejected ? "Review this rejected submission" : "Approve this submission"}>
                    <Button
                      type="primary"
                      icon={isRejected ? <RedoOutlined /> : <CheckOutlined />}
                      size="small"
                      onClick={() => openActionModal(submissionRecord, isRejected ? 'reapprove' : 'approve')}
                      style={{ background: isRejected ? '#14d0fa' : '#52c41a', borderColor: isRejected ? '#faad14' : '#52c41a' }}
                    >
                      {isRejected ? 'Review' : 'Approve'}
                    </Button>
                  </Tooltip>

                  {!isRejected && (
                    <Tooltip title="Reject this submission">
                      <Button
                        danger
                        type="primary"
                        icon={<CloseOutlined />}
                        size="small"
                        onClick={() => openActionModal(submissionRecord, 'reject')}
                      />
                    </Tooltip>
                  )}

                  {!isRejected && (
                    <Tooltip title="Escalate (SLA breach)">
                      <Button
                        type="default"
                        icon={<ArrowUpOutlined />}
                        size="small"
                        onClick={() => openActionModal(submissionRecord, 'escalate')}
                        style={{ color: '#faad14', borderColor: '#faad14' }}
                      />
                    </Tooltip>
                  )}
                </>
              )}

              <Tooltip title="View Action History">
                <Button
                  icon={<HistoryOutlined />}
                  size="small"
                  onClick={() => fetchActionHistory(submissionRecord.id)}
                >
                  History
                </Button>
              </Tooltip>

              {isDraft && !isCompleted && !isRejected && (
                <Tag color="default" icon={<FileDoneOutlined />}>Draft</Tag>
              )}
              {isRejected && (
                <Tag color="error" icon={<CloseOutlined />}>Rejected</Tag>
              )}
              {isCompleted && (
                <Tag color="success" icon={<CheckOutlined />}>Approved</Tag>
              )}
              {!canTakeActionOnSubmission && !isCompleted && !isDraft && !isRejected && !isDMADeskUserRole && (
                <Tag color="default">Read Only</Tag>
              )}
              {isDMADeskUserRole && (
                <Tag color="blue" icon={<EyeOutlined />}>View Only</Tag>
              )}
            </Space>
          );
        },
      };

      const allColumns = [
        ...fixedColumns,
        ...locationColumns,
        ...dynamicColumns,
        actionsColumn
      ];

      setDetailsModal({
        visible: true,
        loading: false,
        instance: record,
        submissionData: null,
        allSubmissions: allSubmissions,
        tableColumns: allColumns,
        tableData: allSubmissions,
        selectedRowKeys: [],
        workflowInfo: result.workflow_info,
        canTakeAction: canTakeAction,
        stageInfo: stageInfo,
        isDMADeskUser: isDMADeskUser
      });

    } catch (error) {
      console.error('❌ Failed to load submissions:', error);
      if (error.response?.status === 403) {
        message.error(error.response?.data?.message || 'Access denied to this form');
        setDetailsModal({ visible: false, loading: false, instance: null, submissionData: null, allSubmissions: [], tableColumns: [], tableData: [], selectedRowKeys: [], isDMADeskUser: false });
      } else {
        message.error('Failed to load submissions: ' + (error.response?.data?.message || error.message));
        setDetailsModal({
          visible: true,
          loading: false,
          instance: record,
          submissionData: null,
          allSubmissions: [],
          tableColumns: [],
          tableData: [],
          selectedRowKeys: [],
          isDMADeskUser: false
        });
      }
    }
  };

  const openActionModal = (submission, action) => {
    if (userInfo.roleType === 'DMA_DESK_USER') {
      message.warning('DMA Desk Users have view-only access and cannot take actions.');
      return;
    }

    setActionModal({
      visible: true,
      submission: submission,
      action: action,
      comments: ''
    });
  };

  const handleActionWithComment = async () => {
    const { submission, action, comments } = actionModal;

    if (!submission) return;

    if (userInfo.roleType === 'DMA_DESK_USER') {
      message.warning('DMA Desk Users have view-only access and cannot take actions.');
      setActionModal({ visible: false, submission: null, action: null, comments: '' });
      return;
    }

    try {
      setActionLoading(true);

      const instanceId = detailsModal.instance?.id;

      if (!instanceId) {
        message.error('No workflow instance found');
        return;
      }

      const actionType = action === 'escalate' ? 'escalate' : action;

      console.log(`📝 Processing ${actionType} for submission:`, submission.id);

      const response = await api.post('/workflow/submission-action', {
        workflow_instance_id: instanceId,
        submission_id: submission.id,
        action: actionType,
        comments: comments || `-`
      });

      if (response.data.success) {
        const successMessage = actionType === 'review' ? 'review' :
                              actionType.charAt(0).toUpperCase() + actionType.slice(1);
        message.success(`${successMessage} processed successfully`);

        await refreshSubmissionDetails();
        loadData(true);

        setActionModal({ visible: false, submission: null, action: null, comments: '' });
      } else {
        message.error(`Failed to ${action}: ` + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error(`❌ Failed to ${action}:`, error);
      message.error(`Failed to ${action}: ` + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const onSelectChange = (selectedRowKeys) => {
    setDetailsModal(prev => ({ ...prev, selectedRowKeys }));
  };

  const columns = [
    {
      title: 'Form',
      dataIndex: 'form_name',
      key: 'form_name',
      width: 250,
      render: (text, record) => {
        const displayName = text || record.form_name || (record.form_id ? `Form #${record.form_id}` : 'Unknown Form');
        return (
          <div>
            <div className="form-name" style={{ fontWeight: 'bold' }}>
              {displayName}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Submitted On',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => {
        if (!date) return '-';
        const d = new Date(date);
        return (
          <div>
            <div>{d.toLocaleDateString()}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{d.toLocaleTimeString()}</div>
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => viewSubmissionDetails(record)}
        >
          View Details
        </Button>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys: detailsModal.selectedRowKeys,
    onChange: onSelectChange,
  };

  const getUserRoleDisplay = () => {
    if (userInfo.roleType === 'DMA_DESK_OFFICER') {
      return (
        <Space size="small" style={{ marginLeft: 12 }}>
          <Tag icon={<DesktopOutlined />} color="purple">
            DMA Desk Officer
          </Tag>
          {userInfo.deskIds && userInfo.deskIds.length > 0 && (
            <Tag color="purple">
              Desks: {userInfo.deskIds.join(', ')}
            </Tag>
          )}
          {userInfo.deskIds && userInfo.deskIds.length > 1 && (
            <Tag color="green">
              Total Assigned Desks: {userInfo.deskIds.length}
            </Tag>
          )}
        </Space>
      );
    }
    if (userInfo.roleType === 'DMA_DESK_USER') {
      return (
        <Space size="small" style={{ marginLeft: 12 }}>
          <Tag icon={<DesktopOutlined />} color="blue">
            DMA Desk User (View Only)
          </Tag>
          {userInfo.deskIds && userInfo.deskIds.length > 0 && (
            <Tag color="blue">
              Desks: {userInfo.deskIds.join(', ')}
            </Tag>
          )}
          {userInfo.deskIds && userInfo.deskIds.length > 1 && (
            <Tag color="green">
              Total Assigned Desks: {userInfo.deskIds.length}
            </Tag>
          )}
        </Space>
      );
    }
    return null;
  };

  return (
    <div className="workflow-dashboard-container">
      <Navbar />

      <div className="workflow-main-content">
        <Content className="dashboard-content">
          <Card className="header-card">
            <div className="header-content">
              <div className="header-left">
                <div className="user-info">
                  <div className="user-info-inner">
                    <Title level={4} className="dashboard-title">
                      <FileDoneOutlined className="title-icon" />
                      Validation Flow Dashboard
                    </Title>
                    {getUserRoleDisplay()}
                  </div>
                </div>

                <div className="header-search">
                  <Input
                    placeholder="Search by form name, ID, status..."
                    prefix={<SearchOutlined className="search-icon" />}
                    onChange={handleSearchChange}
                    value={searchText}
                    allowClear
                    className="search-input"
                  />
                  {searchText && (
                    <Text type="secondary" className="search-count">
                      {filteredInstances.length} result(s)
                    </Text>
                  )}
                </div>
              </div>

              <Space direction={isMobile ? "vertical" : "horizontal"} size="middle">
                <Space size="small">
                  <Tooltip title="Auto-refresh dashboard">
                    <Switch
                      checked={autoRefreshEnabled}
                      onChange={setAutoRefreshEnabled}
                      checkedChildren={<SyncOutlined spin />}
                      unCheckedChildren={<SyncOutlined />}
                    />
                  </Tooltip>

                  {autoRefreshEnabled && (
                    <>
                      <Radio.Group
                        size="small"
                        value={refreshInterval}
                        onChange={(e) => setRefreshInterval(e.target.value)}
                        buttonStyle="solid"
                      >
                        <Radio.Button value={15}>15s</Radio.Button>
                        <Radio.Button value={30}>30s</Radio.Button>
                        <Radio.Button value={60}>1m</Radio.Button>
                        <Radio.Button value={120}>2m</Radio.Button>
                      </Radio.Group>

                      {refreshCountdown > 0 && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Next refresh in {refreshCountdown}s
                        </Text>
                      )}
                    </>
                  )}
                </Space>

                {lastRefreshTime && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Last refresh: {lastRefreshTime.toLocaleTimeString()}
                  </Text>
                )}

                <Button
                  icon={<ReloadOutlined spin={loading} />}
                  onClick={refreshData}
                  size={isMobile ? "small" : "middle"}
                  className="refresh-btn"
                >
                  Refresh
                </Button>
              </Space>
            </div>
          </Card>

          <Card className="main-table-card">
            <Table
              columns={columns}
              dataSource={filteredInstances}
              loading={loading}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: !isMobile,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
              }}
              locale={{
                emptyText: searchText ? (
                  <div className="empty-state">
                    <SearchOutlined className="empty-icon" />
                    <div>No results found for "{searchText}"</div>
                    <Button type="link" onClick={() => setSearchText('')}>Clear search</Button>
                  </div>
                ) : (
                  <div className="empty-state">
                    <FileDoneOutlined className="empty-icon" />
                    <div>No workflow instances found</div>
                  </div>
                )
              }}
              scroll={isMobile ? { x: 800 } : { x: 1000 }}
              size="middle"
              className="workflow-table"
            />
          </Card>
        </Content>
      </div>

      <Modal
        title={
          <div className="modal-title">
            <DatabaseOutlined className="modal-title-icon" />
            <span>
              Submission Details: {detailsModal.instance?.form_name ||
                (detailsModal.instance?.form_id ? `Form #${detailsModal.instance.form_id}` : 'Unknown Form')}
            </span>
            {detailsModal.isDMADeskUser && (
              <Tag color="blue" icon={<EyeOutlined />} style={{ marginLeft: 12 }}>
                View Only Mode
              </Tag>
            )}
          </div>
        }
        open={detailsModal.visible}
        onCancel={() => {
          setDetailsModal({ visible: false, loading: false, instance: null, submissionData: null, allSubmissions: [], tableColumns: [], tableData: [], selectedRowKeys: [], workflowData: null, canTakeAction: false, currentStage: null, stageInfo: null, isDMADeskUser: false });
          setSubmissionFilters({ division: null, district: null, ulb: null });
        }}
        footer={null}
        width={isMobile ? '95%' : '90%'}
        className="submission-modal"
        style={{ top: 20 }}
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto', padding: '20px' } }}
      >
        {detailsModal.loading ? (
          <div className="modal-loading" style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading submission data...</div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, textAlign: 'right', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={refreshSubmissionDetails}
                  size="small"
                >
                  Refresh Data
                </Button>

                {userInfo.roleType === 'DMA_DESK_OFFICER' && detailsModal.canTakeAction && !detailsModal.isDMADeskUser && (
                  <Tooltip title="Approve all pending submissions by division">
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      onClick={openBulkApproveModal}
                      size="small"
                      style={{ background: '#faad14', borderColor: '#faad14' }}
                    >
                      Bulk Approve by Division
                    </Button>
                  </Tooltip>
                )}
              </Space>
            </div>

            {detailsModal.instance && detailsModal.stageInfo && (
              <Card size="small" className="instance-info-card" style={{ marginBottom: 16 }}>
                <Descriptions column={isMobile ? 1 : 2} size="small">
                  <Descriptions.Item label="Form">
                    <strong>{detailsModal.instance.form_name}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Current Stage" span={2}>
                    <div>
                      <Progress
                        percent={detailsModal.stageInfo.progress}
                        size="small"
                        strokeColor="#1890ff"
                        format={() => `${detailsModal.stageInfo.currentIndex}/${detailsModal.stageInfo.totalStages}`}
                      />
                      <Steps
                        current={detailsModal.stageInfo.currentIndex}
                        size="small"
                        style={{ marginTop: 16 }}
                      >
                        {detailsModal.stageInfo.workflowSequence.map((stage, idx) => (
                          <Step
                            key={idx}
                            title={stage}
                            status={
                              idx < detailsModal.stageInfo.currentIndex ? 'finish' :
                              idx === detailsModal.stageInfo.currentIndex ? 'process' :
                              'wait'
                            }
                            icon={
                              idx < detailsModal.stageInfo.currentIndex ? <CheckOutlined /> :
                              idx === detailsModal.stageInfo.currentIndex ? <FieldTimeOutlined /> :
                              null
                            }
                          />
                        ))}
                      </Steps>
                    </div>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={
                      detailsModal.instance.status === 'approved' ? 'success' :
                      detailsModal.instance.status === 'rejected' ? 'error' :
                      detailsModal.instance.status === 'in_progress' ? 'processing' :
                      detailsModal.instance.status === 'escalated' ? 'red' : 'default'
                    }>
                      {detailsModal.instance.status?.toUpperCase() || 'UNKNOWN'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Your Permission">
                    {detailsModal.isDMADeskUser ? (
                      <Tag color="blue" icon={<EyeOutlined />}>View Only (DMA Desk User)</Tag>
                    ) : detailsModal.canTakeAction ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>✓ You can approve/reject</Tag>
                    ) : (
                      <Tag color="red" icon={<StopOutlined />}>✗ Read only</Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Submissions">
                    <Badge count={detailsModal.allSubmissions?.length || 0} showZero style={{ backgroundColor: '#1890ff' }} />
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {(availableFilters.divisions.length > 0 || availableFilters.districts.length > 0 || availableFilters.ulbs.length > 0) && (
              <Card size="small" className="filter-card" style={{ marginBottom: 16, background: "linear-gradient(120deg,  #0F172A 10%, #0F766E 100%)" }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SearchOutlined style={{ color: '#1890ff' }} />
                    <strong style={{ color: '#fbfbfa' }}>Filter Submissions:</strong>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    {availableFilters.divisions.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ApartmentOutlined style={{ color: '#faad14' }} />
                        <select
                          value={submissionFilters.division || ''}
                          onChange={(e) => handleFilterChange('division', e.target.value || null)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #d9d9d9',
                            minWidth: '120px',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">All Divisions</option>
                          {availableFilters.divisions.map(div => (
                            <option key={div} value={div}>{div}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {availableFilters.districts.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <EnvironmentOutlined style={{ color: '#52c41a' }} />
                        <select
                          value={submissionFilters.district || ''}
                          onChange={(e) => handleFilterChange('district', e.target.value || null)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #d9d9d9',
                            minWidth: '140px',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            cursor: 'pointer'
                          }}
                          disabled={submissionFilters.division && cascadingFilters.filteredDistricts.length === 0}
                        >
                          <option value="">All Districts</option>
                          {(submissionFilters.division ? cascadingFilters.filteredDistricts : availableFilters.districts).map(dist => (
                            <option key={dist} value={dist}>{dist}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {availableFilters.ulbs.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BankOutlined style={{ color: '#1890ff' }} />
                        <select
                          value={submissionFilters.ulb || ''}
                          onChange={(e) => handleFilterChange('ulb', e.target.value || null)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: '1px solid #d9d9d9',
                            minWidth: '160px',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            cursor: 'pointer'
                          }}
                          disabled={(submissionFilters.division || submissionFilters.district) && cascadingFilters.filteredUlbs.length === 0}
                        >
                          <option value="">All ULBs</option>
                          {(submissionFilters.division || submissionFilters.district ? cascadingFilters.filteredUlbs : availableFilters.ulbs).map(ulb => (
                            <option key={ulb} value={ulb}>{ulb}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {(submissionFilters.division || submissionFilters.district || submissionFilters.ulb) && (
                      <Button
                        size="small"
                        onClick={clearAllFilters}
                        style={{ marginLeft: '8px' }}
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {(submissionFilters.division || submissionFilters.district || submissionFilters.ulb) && (
              <div style={{ marginBottom: 16, fontSize: '12px', color: '#666' }}>
                <span>Active filters: </span>
                {submissionFilters.division && (
                  <Tag icon={<ApartmentOutlined />} closable onClose={() => handleFilterChange('division', null)}>
                    Division: {submissionFilters.division}
                  </Tag>
                )}
                {submissionFilters.district && (
                  <Tag icon={<EnvironmentOutlined />} closable onClose={() => handleFilterChange('district', null)}>
                    District: {submissionFilters.district}
                  </Tag>
                )}
                {submissionFilters.ulb && (
                  <Tag icon={<BankOutlined />} closable onClose={() => handleFilterChange('ulb', null)}>
                    ULB: {submissionFilters.ulb}
                  </Tag>
                )}
                <span style={{ marginLeft: '8px' }}>
                  Showing {detailsModal.tableData?.length || 0} of {detailsModal.allSubmissions?.length || 0} submissions
                </span>
              </div>
            )}

            <Divider className="modal-divider">Submission Data</Divider>

            {detailsModal.tableData && detailsModal.tableData.length > 0 ? (
              <Table
                rowSelection={detailsModal.canTakeAction && !detailsModal.isDMADeskUser ? rowSelection : undefined}
                columns={detailsModal.tableColumns}
                dataSource={detailsModal.tableData}
                rowKey="id"
                pagination={{
                  current: submissionsCurrentPage,
                  pageSize: submissionsPageSize,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onChange: (page, pageSize) => {
                    setSubmissionsCurrentPage(page);
                    setSubmissionsPageSize(pageSize);
                  },
                  onShowSizeChange: (current, size) => {
                    setSubmissionsCurrentPage(1);
                    setSubmissionsPageSize(size);
                  }
                }}
                size="small"
                scroll={{ x: 'max-content', y: 400 }}
                loading={actionLoading}
                className="submissions-table"
              />
            ) : (
              <div className="no-data-state" style={{ textAlign: 'center', padding: '40px' }}>
                <DatabaseOutlined style={{ fontSize: 48, color: '#ccc' }} />
                <div style={{ marginTop: 16 }}>No submission data found</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Bulk Approve Modal */}
      <Modal
        title={
          <div>
            <ThunderboltOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Bulk Approve by Division
          </div>
        }
        open={bulkApproveModal.visible}
        onCancel={() => setBulkApproveModal(prev => ({ ...prev, visible: false, selectedDivision: null, pendingSubmissions: [], comments: '' }))}
        footer={[
          <Button key="cancel" onClick={() => setBulkApproveModal(prev => ({ ...prev, visible: false, selectedDivision: null, pendingSubmissions: [], comments: '' }))}>
            Cancel
          </Button>,
          <Popconfirm
            key="submit"
            title="Confirm Bulk Approval"
            description={`Are you sure you want to approve ${bulkApproveModal.pendingSubmissions?.length || 0} submissions in ${bulkApproveModal.selectedDivision || 'selected division'}?`}
            onConfirm={handleBulkApprove}
            okText="Yes, Approve All"
            cancelText="No"
            okButtonProps={{ loading: bulkApproveModal.loading }}
          >
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={!bulkApproveModal.selectedDivision || bulkApproveModal.pendingSubmissions?.length === 0}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Approve {bulkApproveModal.pendingSubmissions?.length || 0} Submissions
            </Button>
          </Popconfirm>
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>Select Division:</strong>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Select a division"
            value={bulkApproveModal.selectedDivision}
            onChange={handleDivisionChange}
            showSearch
            optionFilterProp="children"
          >
            {bulkApproveModal.divisions.map(division => (
              <Option key={division} value={division}>
                <Space>
                  <ApartmentOutlined style={{ color: '#faad14' }} />
                  {division}
                </Space>
              </Option>
            ))}
          </Select>
        </div>

        {bulkApproveModal.selectedDivision && bulkApproveModal.pendingSubmissions.length > 0 && (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong>Pending Submissions:</strong>
              <div style={{
                maxHeight: '300px',
                overflow: 'auto',
                marginTop: 8,
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                padding: '12px'
              }}>
                {bulkApproveModal.pendingSubmissions.map(sub => (
                  <div key={sub.id} style={{
                    padding: '8px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <Tag color="blue">#{sub.id}</Tag>
                      <span style={{ marginLeft: 8 }}>
                        <BankOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                        {sub.ulb_name || sub.user_ulb_name || `ULB ID: ${sub.ulb_id}`}
                      </span>
                    </div>
                    <div>
                      <Tag color="orange">{sub.user_role || 'ULB'}</Tag>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>Comments (optional):</strong>
              <TextArea
                rows={3}
                placeholder="Add comments for bulk approval..."
                value={bulkApproveModal.comments}
                onChange={(e) => setBulkApproveModal(prev => ({ ...prev, comments: e.target.value }))}
                style={{ marginTop: 8 }}
              />
            </div>

            <Alert
              message="Bulk Approval Information"
              description={`This will approve ${bulkApproveModal.pendingSubmissions.length} submission(s) in ${bulkApproveModal.selectedDivision} division. Each submission will be moved to the next workflow stage or marked as completed if it's the final stage.`}
              type="info"
              showIcon
            />
          </>
        )}

        {bulkApproveModal.selectedDivision && bulkApproveModal.pendingSubmissions.length === 0 && (
          <Alert
            message="No Pending Submissions"
            description={`No pending submissions found in ${bulkApproveModal.selectedDivision} division.`}
            type="warning"
            showIcon
          />
        )}
      </Modal>

      <Modal
        title={
          <div>
            {actionModal.action === 'approve' && <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />}
            {actionModal.action === 'reapprove' && <RedoOutlined style={{ color: '#faad14', marginRight: 8 }} />}
            {actionModal.action === 'reject' && <CloseOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />}
            {actionModal.action === 'escalate' && <ArrowUpOutlined style={{ color: '#faad14', marginRight: 8 }} />}
            <span>
              {actionModal.action === 'approve' && 'Approve Submission'}
              {actionModal.action === 'reapprove' && 'Review Submission'}
              {actionModal.action === 'reject' && 'Reject Submission'}
              {actionModal.action === 'escalate' && 'Escalate Submission'}
            </span>
          </div>
        }
        open={actionModal.visible}
        onCancel={() => setActionModal({ visible: false, submission: null, action: null, comments: '' })}
        footer={[
          <Button key="cancel" onClick={() => setActionModal({ visible: false, submission: null, action: null, comments: '' })}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleActionWithComment}
            loading={actionLoading}
            danger={actionModal.action === 'reject'}
            style={actionModal.action === 'approve' ? { background: '#52c41a', borderColor: '#52c41a' } :
                   actionModal.action === 'reapprove' ? { background: '#faad14', borderColor: '#faad14', color: '#fff' } :
                   actionModal.action === 'escalate' ? { background: '#faad14', borderColor: '#faad14' } : {}}
          >
            {actionModal.action === 'approve' && 'Approve'}
            {actionModal.action === 'reapprove' && 'Review'}
            {actionModal.action === 'reject' && 'Reject'}
            {actionModal.action === 'escalate' && 'Escalate'}
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>Current Stage:</strong>{' '}
          <Tag color="blue">{actionModal.submission?.current_stage || 'ULB'}</Tag>
        </div>
        <div style={{ marginBottom: 16 }}>
          <strong>Current Status:</strong>{' '}
          <Tag color={
            actionModal.submission?.workflow_status === 'rejected' ? 'error' :
            actionModal.submission?.workflow_status === 'approved' ? 'success' :
            'processing'
          }>
            {actionModal.submission?.workflow_status?.toUpperCase() || 'IN_PROGRESS'}
          </Tag>
        </div>
        <div style={{ marginBottom: 16 }}>
          <strong>Action:</strong>{' '}
          <Tag color={
            actionModal.action === 'approve' ? 'success' :
            actionModal.action === 'reapprove' ? 'warning' :
            actionModal.action === 'reject' ? 'error' :
            'warning'
          }>
            {actionModal.action?.toUpperCase()}
          </Tag>
        </div>
        <div>
          <strong>Comments (optional):</strong>
          <TextArea
            rows={4}
            placeholder={actionModal.action === 'reject' ? "Please provide reason for rejection..." :
                        actionModal.action === 'reapprove' ? "Please provide reason for review..." :
                        "Add any comments here..."}
            value={actionModal.comments}
            onChange={(e) => setActionModal({ ...actionModal, comments: e.target.value })}
            style={{ marginTop: 8 }}
          />
        </div>
        {actionModal.action === 'reapprove' && (
          <Alert
            message="Review Information"
            description="This submission was previously rejected. Reviewing will change its status back to 'In Progress' and continue the workflow from the current stage."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        {actionModal.action === 'escalate' && (
          <Alert
            message="Escalation Warning"
            description="Escalating this submission will move it to the next approval stage immediately due to SLA breach or urgency."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Modal>

      <Modal
        title={
          <div>
            <HistoryOutlined style={{ marginRight: 8 }} />
            Action History - Submission #{historyModal.submissionId}
          </div>
        }
        open={historyModal.visible}
        onCancel={() => setHistoryModal({ visible: false, submissionId: null, actions: [], loading: false })}
        footer={null}
        width={700}
      >
        {historyModal.loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin />
            <div style={{ marginTop: 16 }}>Loading action history...</div>
          </div>
        ) : historyModal.actions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <HistoryOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div>No actions recorded yet for this submission</div>
          </div>
        ) : (
          <Timeline mode="left">
            {historyModal.actions.map((action, idx) => (
              <Timeline.Item
                key={idx}
                label={action.created_at ? new Date(action.created_at).toLocaleString() : 'Unknown date'}
                color={
                  action.action === 'approve' ? 'green' :
                  action.action === 'reapprove' ? 'orange' :
                  action.action === 'reject' ? 'red' :
                  action.action === 'escalate' ? 'orange' :
                  'blue'
                }
                dot={
                  action.action === 'approve' ? <CheckCircleOutlined /> :
                  action.action === 'reapprove' ? <RedoOutlined /> :
                  action.action === 'reject' ? <CloseOutlined /> :
                  action.action === 'escalate' ? <ArrowUpOutlined /> :
                  <UserOutlined />
                }
              >
                <Card size="small">
                  <div style={{ marginBottom: 8 }}>
                    <strong>Action:</strong>{' '}
                    <Tag color={
                      action.action === 'approve' ? 'success' :
                      action.action === 'reapprove' ? 'warning' :
                      action.action === 'reject' ? 'error' :
                      action.action === 'escalate' ? 'warning' :
                      'default'
                    }>
                      {action.action?.toUpperCase() || 'UNKNOWN'}
                    </Tag>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Performed By:</strong>{' '}
                    {action.performed_by_name || action.performed_by_user_name || getUserName(action)}
                    {action.performed_by_role && (
                      <Tag color="default" style={{ marginLeft: 8 }}>
                        {action.performed_by_role}
                      </Tag>
                    )}
                  </div>
                  {action.comments && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>Comments:</strong> {action.comments}
                    </div>
                  )}
                  {action.stage_name && (
                    <div style={{ marginBottom: 8 }}>
                      <strong>Stage:</strong> {action.stage_name}
                    </div>
                  )}
                </Card>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Modal>

      {/* FIXED: Data View Modal with refs for persistence */}
{/* FIXED: Data View Modal with record preservation */}
<Modal
  title={
    <div>
      <EyeOutlined style={{ marginRight: 8, color: '#1890ff' }} />
      Detailed View - Submission #{dataViewModal.record?.id || dataViewRecordRef.current?.id}
      {fieldOrderLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
      {formFieldOrder.length > 0 && !fieldOrderLoading && (
        <Tag color="green" style={{ marginLeft: 8 }}>
          {formFieldOrder.length} fields loaded
        </Tag>
      )}
    </div>
  }
  open={dataViewModal.visible}
  onCancel={() => {
    setDataViewModal({ visible: false, record: null, loading: false });
    dataViewRecordRef.current = null;
  }}
  footer={[
    <Button 
      key="refresh" 
      icon={<ReloadOutlined />} 
      onClick={async () => {
        setDataViewModal(prev => ({ ...prev, loading: true }));
        const recordToRefresh = dataViewModal.record || dataViewRecordRef.current;
        if (recordToRefresh) {
          await viewRowData(recordToRefresh);
        }
        await refreshSubmissionDetails();
      }}
      loading={dataViewModal.loading}
    >
      Refresh Data
    </Button>,
    <Button key="close" onClick={() => {
      setDataViewModal({ visible: false, record: null, loading: false });
      dataViewRecordRef.current = null;
    }}>
      Close
    </Button>
  ]}
  width={800}
  className="data-view-modal"
  style={{ top: 20 }}
  styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
>
  {dataViewModal.loading || fieldOrderLoading ? (
    <div style={{ textAlign: 'center', padding: '40px' }}>
      <Spin size="large" />
      <div style={{ marginTop: 16, color: '#666' }}>
        {fieldOrderLoading ? 'Loading field structure...' : 'Loading data...'}
      </div>
    </div>
  ) : (
    // IMPORTANT: Use record from state OR from ref - always use ref as fallback
    renderVerticalData(dataViewModal.record || dataViewRecordRef.current)
  )}
</Modal>
    </div>
  );
};

export default WorkflowDashboard;